// Slim alert mounted on Home below PlanMismatchBanner. Surfaces post-fact
// awareness of plan-downgrade reconciliation: "You have N disabled agents
// and M excluded KB docs from a recent plan change → Review."
//
// Hidden when both counters are zero — the common case after a clean
// upgrade or for tenants who never downgraded. Click on the link routes
// to /agents (where disabled rows show with the per-row Enable CTA).
//
// Copy split between the agent-only, kb-only, and combined cases keeps
// the message specific without templating gymnastics in the JSX.

import { Link } from 'react-router-dom';
import { Archive } from 'lucide-react';

interface Props {
  disabledAgentCount: number;
  excludedKbDocCount: number;
}

export function DisabledResourcesBanner({
  disabledAgentCount,
  excludedKbDocCount,
}: Props) {
  if (disabledAgentCount <= 0 && excludedKbDocCount <= 0) return null;

  const heading = headingFor(disabledAgentCount, excludedKbDocCount);
  const body =
    disabledAgentCount > 0 && excludedKbDocCount > 0
      ? 'A recent plan change put your tenant over its caps. Review and re-enable on upgrade — or remove items you no longer need.'
      : disabledAgentCount > 0
        ? 'A recent plan change put your tenant over the agent cap. Re-enable on upgrade — or remove agents you no longer need.'
        : 'A recent plan change put your tenant over the KB document cap. Re-include on upgrade — or remove docs you no longer need.';

  return (
    <div role="alert" className="rounded-md border border-warning/30 bg-warning/10 px-4 py-3 text-sm">
      <div className="flex items-start gap-2">
        <Archive className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
        <div className="min-w-0 flex-1 space-y-1">
          <p className="font-medium">{heading}</p>
          <p className="text-muted-foreground">{body}</p>
          <div className="pt-1">
            {disabledAgentCount > 0 && (
              <Link to="/agents" className="underline-offset-2 hover:underline">
                Review agents →
              </Link>
            )}
            {disabledAgentCount > 0 && excludedKbDocCount > 0 && (
              <span className="px-2 text-muted-foreground/40" aria-hidden>·</span>
            )}
            {excludedKbDocCount > 0 && (
              <Link to="/agents" className="underline-offset-2 hover:underline">
                Review KB documents →
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function headingFor(agentCount: number, kbCount: number): string {
  if (agentCount > 0 && kbCount > 0) {
    return `${agentCount.toLocaleString()} ${agentCount === 1 ? 'agent' : 'agents'} · ${kbCount.toLocaleString()} ${kbCount === 1 ? 'document' : 'documents'} disabled`;
  }
  if (agentCount > 0) {
    return agentCount === 1 ? '1 agent disabled' : `${agentCount.toLocaleString()} agents disabled`;
  }
  return kbCount === 1 ? '1 KB document excluded' : `${kbCount.toLocaleString()} KB documents excluded`;
}
